/*
 * Free gift(s) with purchase — up to two independent promos:
 *   - "every": present whenever the cart holds at least one real (non-gift) item
 *   - "min":   present whenever the real item subtotal reaches a configured minimum
 *
 * Invariant: each promo's gift line is in the cart if and only if its condition holds.
 * Enforced after every cart mutation, so it self-heals no matter which add-to-cart path ran.
 *
 * Config comes from window.FREE_GIFTS = { every, min }, printed by layout/theme.liquid.
 * Either key is null when that promo is off or unconfigured.
 */
(function () {
  var CFG = window.FREE_GIFTS || {};
  var PROMOS = [];

  if (CFG.every && CFG.every.id) {
    PROMOS.push({
      key: 'every',
      id: Number(CFG.every.id),
      handle: CFG.every.handle || '',
      title: CFG.every.title || '',
      image: CFG.every.image || '',
      toast: CFG.every.toast || 'Free gift added to your cart.'
    });
  }
  if (CFG.min && CFG.min.id) {
    PROMOS.push({
      key: 'min',
      id: Number(CFG.min.id),
      handle: CFG.min.handle || '',
      title: CFG.min.title || '',
      image: CFG.min.image || '',
      amount: Number(CFG.min.amount) || 0,
      toast: CFG.min.toast || 'Free gift added to your cart.'
    });
  }

  if (!PROMOS.length) return;

  var GIFT_IDS = PROMOS.map(function (p) { return p.id; });
  var GIFT_HANDLES = PROMOS.map(function (p) { return p.handle; }).filter(Boolean);

  /* Matches the cart mutation endpoints, with or without the .js extension. */
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  /* Bound up front so our own requests never re-enter the interceptor below. */
  var nativeFetch = window.fetch.bind(window);

  var busy = false;
  var pending = null;
  var reloadTimer = null;

  function noop() {}

  function getCart() {
    return nativeFetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (res) {
      return res.json();
    });
  }

  /* Rejects on a non-2xx so a failed gift add can't be reported as a success. */
  function postCart(url, body) {
    return nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw new Error((data && data.description) || 'Cart request failed');
        return data;
      });
    });
  }

  /* ============ CART COUNT ============ */

  function syncCount(cart) {
    var count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
    });
    document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach(function (el) {
      el.textContent = count;
    });
  }

  /* Ask the drawer / cart page to re-render from the live cart. */
  function broadcast(cart) {
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  }

  /*
   * Reload so the theme's empty-cart state renders. Deferred and re-checked because flows
   * like "Buy it now" clear the cart and immediately re-add to it — an instant reload would
   * abort them. Any new mutation cancels a pending reload.
   */
  function scheduleReload() {
    clearTimeout(reloadTimer);
    reloadTimer = setTimeout(function () {
      getCart()
        .then(function (cart) {
          if (!cart.item_count) window.location.reload();
        })
        .catch(noop);
    }, 400);
  }

  /* ============ CONDITION PER PROMO ============ */

  function isUnlocked(promo, reals, realSubtotalCents) {
    if (promo.key === 'min') return reals >= 1 && realSubtotalCents >= promo.amount;
    return reals >= 1;
  }

  /* ============ THE INVARIANT ============ */

  function enforce(opts) {
    opts = opts || {};

    if (busy) {
      /* Collapse overlapping requests; a loud trigger wins over a silent one. */
      pending = pending ? { silent: pending.silent && opts.silent } : opts;
      return Promise.resolve();
    }
    busy = true;

    return getCart()
      .then(function (cart) {
        var giftLines = {};
        var reals = 0;

        (cart.items || []).forEach(function (item) {
          var variantId = Number(item.variant_id);
          var giftIndex = GIFT_IDS.indexOf(variantId);
          if (giftIndex !== -1) {
            giftLines[PROMOS[giftIndex].key] = item;
          } else {
            reals += 1;
          }
        });

        var realSubtotal = typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price : 0;

        var toAdd = [];
        var toRemove = [];

        PROMOS.forEach(function (promo) {
          var unlocked = isUnlocked(promo, reals, realSubtotal);
          var line = giftLines[promo.key];
          if (unlocked && !line) toAdd.push(promo);
          if (!unlocked && line) toRemove.push(line);
        });

        if (!toAdd.length && !toRemove.length) {
          syncCount(cart);
          return;
        }

        var chain = Promise.resolve();
        toAdd.forEach(function (promo) {
          chain = chain.then(function () {
            return postCart('/cart/add.js', { id: promo.id, quantity: 1 });
          });
        });
        toRemove.forEach(function (line) {
          chain = chain.then(function () {
            return postCart('/cart/change.js', { id: line.key, quantity: 0 });
          });
        });

        return chain
          .then(getCart)
          .then(function (updated) {
            syncCount(updated);
            broadcast(updated);
            if (!opts.silent) {
              if (toAdd.length) showUnlockModal(toAdd);
              if (toRemove.length && !updated.item_count) scheduleReload();
            }
          });
      })
      .catch(noop)
      .then(function () {
        busy = false;
        var next = pending;
        pending = null;
        if (next) return enforce(next);
      });
  }

  /* ============ INTERCEPT EVERY CART MUTATION ============ */

  window.fetch = function (input) {
    var url = '';
    if (typeof input === 'string') {
      url = input;
    } else if (input && typeof input.url === 'string') {
      url = input.url;
    } else if (input) {
      url = String(input);
    }

    var isMutation = CART_MUTATION.test(url);
    if (isMutation) clearTimeout(reloadTimer);

    var request = nativeFetch.apply(window, arguments);
    if (!isMutation) return request;

    return request.then(function (res) {
      if (res.ok) enforce({ silent: false });
      return res;
    });
  };

  /* ============ TOAST (fallback when the modal markup is missing) ============ */

  var toastHost = null;

  function ensureToastHost() {
    if (toastHost && document.body.contains(toastHost)) return toastHost;
    toastHost = document.createElement('div');
    toastHost.className = 'free-gift-toasts';
    document.body.appendChild(toastHost);
    return toastHost;
  }

  function showToast(message, title) {
    if (!document.body) return;
    var host = ensureToastHost();

    var toast = document.createElement('div');
    toast.className = 'free-gift-toast';
    toast.setAttribute('role', 'status');
    toast.innerHTML =
      '<span class="free-gift-toast__icon" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
      '<polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/>' +
      '<line x1="12" y1="22" x2="12" y2="7"/>' +
      '<path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>' +
      '<path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>' +
      '</svg></span>' +
      '<span class="free-gift-toast__body">' +
      '<span class="free-gift-toast__title"></span>' +
      '<span class="free-gift-toast__message"></span>' +
      '</span>';
    toast.querySelector('.free-gift-toast__title').textContent = title || 'Free gift added';
    toast.querySelector('.free-gift-toast__message').textContent = message;
    host.appendChild(toast);

    /* Next frame, so the entry transition actually runs. */
    requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });

    setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 400);
    }, 4000);
  }

  /* Reusable by the rest of the theme, e.g. for "Added to cart". */
  window.FREE_GIFTS.showToast = showToast;

  /* ============ CELEBRATION MODAL ============ */

  function defineGiftModal() {
    if (customElements.get('free-gift-modal')) return;

    class FreeGiftModal extends HTMLElement {
      connectedCallback() {
        this.overlay = this.querySelector('[data-free-gift-modal-overlay]');
        this.closeBtn = this.querySelector('[data-free-gift-modal-close]');
        this.ctaBtn = this.querySelector('[data-free-gift-modal-cta]');
        this.cardsHost = this.querySelector('[data-free-gift-modal-cards]');
        this.heading = this.querySelector('[data-free-gift-modal-heading]');

        this.overlay && this.overlay.addEventListener('click', () => this.hide());
        this.closeBtn && this.closeBtn.addEventListener('click', () => this.hide());
        this.ctaBtn && this.ctaBtn.addEventListener('click', () => this.hide());
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !this.hidden) this.hide();
        });
      }

      show(promos) {
        if (!this.cardsHost) return;
        this.heading.textContent = promos.length > 1 ? 'You unlocked free gifts!' : 'You unlocked a free gift!';
        this.cardsHost.innerHTML = promos
          .map(function (promo) {
            var img = promo.image
              ? '<img src="' + promo.image + '" alt="" class="free-gift-modal__card-image" width="48" height="48" loading="lazy">'
              : '<span class="free-gift-modal__card-image free-gift-modal__card-image--placeholder" aria-hidden="true"></span>';
            return (
              '<div class="free-gift-modal__card">' +
              img +
              '<div class="free-gift-modal__card-body">' +
              '<span class="free-gift-modal__card-title">' + (promo.title || 'Free gift') + '</span>' +
              '<span class="free-gift-modal__card-note">Added to your cart, free of charge</span>' +
              '</div>' +
              '</div>'
            );
          })
          .join('');

        this._lastFocused = document.activeElement;
        this.hidden = false;
        document.body.style.overflow = 'hidden';

        requestAnimationFrame(() => {
          this.classList.add('is-visible');
        });
        setTimeout(() => {
          this.closeBtn && this.closeBtn.focus();
        }, 50);
      }

      hide() {
        this.classList.remove('is-visible');
        document.body.style.overflow = '';
        setTimeout(() => {
          this.hidden = true;
        }, 250);
        if (this._lastFocused && this._lastFocused.focus) this._lastFocused.focus();
      }
    }

    customElements.define('free-gift-modal', FreeGiftModal);
  }

  function showUnlockModal(promos) {
    defineGiftModal();
    var modal = document.querySelector('free-gift-modal');
    if (modal && typeof modal.show === 'function') {
      modal.show(promos);
      return;
    }
    /* Fallback: one toast per newly-unlocked promo. */
    promos.forEach(function (promo) {
      showToast(promo.toast, 'Free gift unlocked');
    });
  }

  /* ============ HIDE THE GIFTS FROM PREDICTIVE / QUICK SEARCH ============ */

  /*
   * Matches the exact handle segment only — a substring test would also hide a product
   * like "reconstitution-solution-1".
   */
  function isGiftUrl(href) {
    if (!href || !GIFT_HANDLES.length) return false;
    var match = href.match(/\/products\/([^/?#]+)/);
    return !!match && GIFT_HANDLES.indexOf(decodeURIComponent(match[1])) !== -1;
  }

  function scrubSearchResults(root) {
    if (!GIFT_HANDLES.length) return;
    var scope = root && root.querySelectorAll ? root : document;
    var containers = scope.querySelectorAll(
      'predictive-search, .predictive-search, [data-predictive-search], .custom-header__search-panel'
    );
    containers.forEach(function (container) {
      container.querySelectorAll('a[href*="/products/"]').forEach(function (link) {
        if (!isGiftUrl(link.getAttribute('href'))) return;
        var row =
          link.closest('li') ||
          link.closest('.predictive-search__list-item') ||
          link.closest('article') ||
          link;
        if (row && row.parentNode) row.parentNode.removeChild(row);
      });
    });
  }

  function watchSearchResults() {
    if (!GIFT_HANDLES.length || !document.body) return;
    scrubSearchResults(document);
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length) {
          scrubSearchResults(document);
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ============ BOOT ============ */

  function boot() {
    defineGiftModal();
    watchSearchResults();
    /* Silent: clears/adds stray gift lines left behind by config changes, with no toast/modal. */
    enforce({ silent: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
